<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AlertTriangle, ArrowLeft, BarChart3, CheckCircle2, ChevronLeft, ChevronRight, Clock3, Download, FileJson, FileOutput, FlaskConical, History, RefreshCw, RotateCcw, Search, ShieldCheck, Trash2, UsersRound } from '@lucide/vue'
import { ApiError, api, downloadCsv, downloadFile, downloadJson, isLocalCreatorMode, putJson, postJson } from '../api'
import type { SurveyDetail } from '../types'

type QualityStatus = 'normal' | 'attention' | 'suspect' | 'excluded'
interface Analytics {
  responses: number
  average_duration: number
  variant_a: number
  variant_b: number
  averages: Record<string, number>
  overview: { responses: number; todayNew: number; averageDuration: number; completionRate: number; activeDrafts: number; abnormal: number; excluded: number }
  quality: Record<QualityStatus, number>
  daily: Array<{ day: string; count: number }>
  questions: Array<{ id: string; prompt: string; type: string; answered: number; missing: number; frequencies: Array<{ value: string; label: string; count: number }>; textSamples: string[] }>
  dimensions: Array<{ key: string; label: string; color: string; average: number; sampleSize: number; distribution: Array<{ label: string; count: number }> }>
  sampled: boolean
  analyzedRows: number
}
interface ResponseRow {
  id: string
  version: number
  variant: string
  answers: Record<string, unknown>
  scores: Record<string, number>
  metadata: Record<string, unknown>
  durationSeconds: number
  completedAt: string
  qualityStatus: QualityStatus
  qualityFlags: Array<{ code: string; level: string; message: string }>
  qualityReviewNote?: string | null
}
interface ResponsePage { rows: ResponseRow[]; total: number; page: number; pageSize: number; pages: number }
interface VersionRow { id: string; version: number; change_note?: string | null; published_at?: string | null; created_at: string; review_json?: Record<string, unknown> }

const route = useRoute()
const router = useRouter()
const localMode = isLocalCreatorMode()
const creatorRoot = localMode ? '/local-create' : '/creator'
const survey = ref<SurveyDetail | null>(null)
const analytics = ref<Analytics | null>(null)
const responses = ref<ResponsePage | null>(null)
const versions = ref<VersionRow[]>([])
const loading = ref(true)
const responseLoading = ref(false)
const error = ref('')
const feedback = ref('')
const activeSection = ref<'overview' | 'questions' | 'dimensions' | 'responses' | 'exports' | 'versions'>(
  window.location.hash === '#versions' ? 'versions' : 'overview',
)
const qualityFilter = ref<'all' | QualityStatus>('all')
const fromDate = ref('')
const toDate = ref('')
const versionFilter = ref('')
const responsePage = ref(1)
const includeExcluded = ref(false)

const surveyId = computed(() => String(route.params.id))
const maxDaily = computed(() => Math.max(1, ...(analytics.value?.daily.map((item) => item.count) ?? [1])))
const qualityLabels: Record<QualityStatus, string> = {
  normal: '正常',
  attention: '需要关注',
  suspect: '疑似低质量',
  excluded: '创作者已排除',
}
const flagLabels: Record<string, string> = {
  too_fast: '完成时间过短',
  many_missing: '答案缺失较多',
  straight_line: '大量相同选项',
  repeated_open_text: '开放题文本重复',
  frequent_reentry: '频繁重新进入',
}

function filters(includeQuality = true) {
  const params = new URLSearchParams()
  if (fromDate.value) params.set('from', fromDate.value)
  if (toDate.value) params.set('to', toDate.value)
  if (versionFilter.value) params.set('version', versionFilter.value)
  if (includeQuality && qualityFilter.value !== 'all') params.set('quality', qualityFilter.value)
  if (includeExcluded.value) params.set('includeExcluded', 'true')
  return params
}

async function loadAnalytics() {
  loading.value = true
  error.value = ''
  try {
    const [detail, data, versionRows] = await Promise.all([
      api<SurveyDetail>(`/api/creator/surveys/${surveyId.value}`),
      api<Analytics>(`/api/creator/surveys/${surveyId.value}/analytics?${filters().toString()}`),
      api<VersionRow[]>(`/api/creator/surveys/${surveyId.value}/versions`),
    ])
    survey.value = detail
    analytics.value = data
    versions.value = versionRows
    await loadResponses()
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '数据中心加载失败。'
  } finally {
    loading.value = false
  }
}

async function loadResponses() {
  responseLoading.value = true
  try {
    const params = filters()
    params.set('page', String(responsePage.value))
    params.set('pageSize', '20')
    responses.value = await api<ResponsePage>(`/api/creator/surveys/${surveyId.value}/responses?${params.toString()}`)
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '答卷列表加载失败。'
  } finally {
    responseLoading.value = false
  }
}

async function applyFilters() {
  responsePage.value = 1
  await loadAnalytics()
}

async function changePage(page: number) {
  if (!responses.value || page < 1 || page > responses.value.pages) return
  responsePage.value = page
  await loadResponses()
}

async function reviewQuality(row: ResponseRow, status: QualityStatus) {
  const action = status === 'excluded' ? '排除' : status === 'normal' ? '恢复为正常' : `标记为“${qualityLabels[status]}”`
  if (!window.confirm(`确定${action}这份答卷吗？操作会写入审计记录，但不会删除原始答案。`)) return
  try {
    await putJson(`/api/creator/surveys/${surveyId.value}/responses/${row.id}/quality`, { status })
    feedback.value = `答卷已${action}。`
    await Promise.all([loadResponses(), loadAnalytics()])
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '答卷状态更新失败。'
  }
}

async function rollback(version: number) {
  if (!window.confirm(`将当前草稿恢复为 V${version} 的内容吗？线上已发布版本和历史答卷不会改变。`)) return
  try {
    await postJson(`/api/creator/surveys/${surveyId.value}/rollback`, { version, note: `从数据中心回滚到 V${version}` })
    feedback.value = `已把 V${version} 恢复为新的私密草稿。`
  } catch (err) {
    error.value = err instanceof ApiError ? err.message : '版本回滚失败。'
  }
}

function exportCsv() {
  void downloadCsv(`/api/creator/surveys/${surveyId.value}/export.csv?includeExcluded=${includeExcluded.value}`, `${survey.value?.title || '问卷'}-答卷.csv`)
}

function exportDimensionChart() {
  if (!analytics.value || !survey.value) return
  const canvas = document.createElement('canvas')
  const width = 1200
  const rowHeight = 92
  canvas.width = width
  canvas.height = Math.max(520, 190 + analytics.value.dimensions.length * rowHeight)
  const context = canvas.getContext('2d')
  if (!context) return
  context.fillStyle = '#f8efd9'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#3d2a22'
  context.font = 'bold 42px serif'
  context.fillText(survey.value.title, 70, 75)
  context.font = '24px serif'
  context.fillStyle = '#725d48'
  context.fillText('维度均值 · 心光量域数据中心', 70, 118)
  analytics.value.dimensions.forEach((dimension, index) => {
    const y = 180 + index * rowHeight
    context.fillStyle = '#4d392e'
    context.font = '24px sans-serif'
    context.fillText(dimension.label, 70, y + 29)
    context.fillStyle = '#dfd1b5'
    context.fillRect(290, y, 720, 34)
    context.fillStyle = dimension.color || '#7f3e4d'
    context.fillRect(290, y, 720 * Math.max(0, Math.min(100, dimension.average)) / 100, 34)
    context.fillStyle = '#612f3c'
    context.font = 'bold 25px serif'
    context.fillText(String(dimension.average), 1040, y + 27)
  })
  const link = document.createElement('a')
  link.download = `${survey.value.title}-维度图.png`
  link.href = canvas.toDataURL('image/png')
  link.click()
}

function showSection(section: typeof activeSection.value) {
  activeSection.value = section
  history.replaceState(null, '', section === 'versions' ? '#versions' : window.location.pathname)
}

onMounted(loadAnalytics)
</script>

<template>
  <div class="analytics-page section-pad">
    <header class="analytics-page-header">
      <button type="button" class="analytics-back" @click="router.push(creatorRoot)"><ArrowLeft/>返回工作台</button>
      <div>
        <p class="eyebrow"><BarChart3/>DATA CENTER</p>
        <h1>{{ survey?.title || '问卷数据中心' }}</h1>
        <p>统计、质量标记、答卷管理与导出集中在这里；系统标记不会自动删除任何答案。</p>
      </div>
      <RouterLink v-if="survey" class="button button-ghost" :to="`${creatorRoot}/${survey.id}/edit`">编辑问卷</RouterLink>
    </header>

    <p v-if="error" class="analytics-alert error" role="alert"><AlertTriangle/>{{ error }}</p>
    <p v-if="feedback" class="analytics-alert success" role="status"><CheckCircle2/>{{ feedback }}</p>

    <section class="analytics-filter-bar" aria-label="数据筛选">
      <label><span>开始日期</span><input v-model="fromDate" type="date"/></label>
      <label><span>结束日期</span><input v-model="toDate" type="date"/></label>
      <label><span>问卷版本</span><select v-model="versionFilter"><option value="">全部版本</option><option v-for="item in versions" :key="item.id" :value="item.version">V{{ item.version }}</option></select></label>
      <label><span>答卷质量</span><select v-model="qualityFilter"><option value="all">全部状态</option><option v-for="(label, key) in qualityLabels" :key="key" :value="key">{{ label }}</option></select></label>
      <button type="button" class="button button-dark" @click="applyFilters"><Search/>应用筛选</button>
    </section>

    <nav class="analytics-tabs" aria-label="数据中心栏目">
      <button :class="{ active: activeSection === 'overview' }" @click="showSection('overview')">数据概览</button>
      <button :class="{ active: activeSection === 'questions' }" @click="showSection('questions')">题目分析</button>
      <button :class="{ active: activeSection === 'dimensions' }" @click="showSection('dimensions')">维度分析</button>
      <button :class="{ active: activeSection === 'responses' }" @click="showSection('responses')">答卷管理</button>
      <button :class="{ active: activeSection === 'exports' }" @click="showSection('exports')">导出中心</button>
      <button :class="{ active: activeSection === 'versions' }" @click="showSection('versions')">版本记录</button>
    </nav>

    <div v-if="loading" class="analytics-loading"><span class="loader-orb"/><p>正在汇总问卷数据…</p></div>
    <template v-else-if="analytics">
      <section v-if="activeSection === 'overview'" class="analytics-section">
        <div class="analytics-overview-grid">
          <article><UsersRound/><span>回收数量</span><strong>{{ analytics.overview.responses }}</strong><small>当前筛选范围</small></article>
          <article><CheckCircle2/><span>完成率</span><strong>{{ analytics.overview.completionRate }}%</strong><small>{{ analytics.overview.activeDrafts }} 份服务端草稿</small></article>
          <article><Clock3/><span>平均用时</span><strong>{{ analytics.overview.averageDuration }}s</strong><small>已提交答卷</small></article>
          <article><RefreshCw/><span>今日新增</span><strong>{{ analytics.overview.todayNew }}</strong><small>按中国标准时间</small></article>
          <article class="attention"><AlertTriangle/><span>异常待看</span><strong>{{ analytics.overview.abnormal }}</strong><small>只标记，不删除</small></article>
        </div>
        <div class="analytics-two-column">
          <article class="analytics-card">
            <div class="analytics-card-heading"><div><h2>近 14 日回收趋势</h2><p>用于观察填写量变化，不代表内容质量。</p></div></div>
            <div v-if="analytics.daily.length" class="daily-chart">
              <div v-for="item in analytics.daily" :key="item.day"><span :style="{ height: `${Math.max(6, item.count / maxDaily * 100)}%` }"/><b>{{ item.count }}</b><small>{{ item.day.slice(5) }}</small></div>
            </div>
            <p v-else class="analytics-empty">近 14 日还没有答卷。</p>
          </article>
          <article class="analytics-card">
            <div class="analytics-card-heading"><div><h2>答卷质量分布</h2><p>创作者最终决定是否纳入分析。</p></div></div>
            <div class="quality-summary">
              <div v-for="(label, key) in qualityLabels" :key="key"><span :class="`quality-dot ${key}`"/><b>{{ label }}</b><strong>{{ analytics.quality[key] || 0 }}</strong></div>
            </div>
          </article>
        </div>
        <p v-if="analytics.sampled" class="analytics-sampling-note"><AlertTriangle/>答卷较多，题目与维度图表使用最近 {{ analytics.analyzedRows }} 份答卷生成；概览总数仍为完整统计。</p>
      </section>

      <section v-else-if="activeSection === 'questions'" class="analytics-section question-analysis-list">
        <article v-for="(question, index) in analytics.questions" :key="question.id" class="analytics-card question-analysis-card">
          <header><span>Q{{ index + 1 }}</span><div><h2>{{ question.prompt }}</h2><p>{{ question.type }} · {{ question.answered }} 项答案 · {{ question.missing }} 份未答</p></div></header>
          <div v-if="question.frequencies.length" class="frequency-bars">
            <div v-for="item in question.frequencies" :key="item.value"><span :title="item.label">{{ item.label }}</span><i><b :style="{ width: `${question.answered ? item.count / question.answered * 100 : 0}%` }"/></i><strong>{{ item.count }}</strong></div>
          </div>
          <ul v-else-if="question.textSamples.length" class="text-samples"><li v-for="(sample, sampleIndex) in question.textSamples" :key="sampleIndex">{{ sample }}</li></ul>
          <p v-else class="analytics-empty">当前筛选范围内还没有可统计答案。</p>
        </article>
      </section>

      <section v-else-if="activeSection === 'dimensions'" class="analytics-section">
        <div class="analytics-section-tools"><div><h2>维度均值与分布</h2><p>分数解释必须结合量表定义与适用范围。</p></div><button class="button button-ghost" @click="exportDimensionChart"><Download/>下载 PNG</button></div>
        <div class="dimension-analysis-grid">
          <article v-for="dimension in analytics.dimensions" :key="dimension.key" class="analytics-card dimension-analysis-card">
            <header><div><h2>{{ dimension.label }}</h2><p>{{ dimension.sampleSize }} 份有效分数</p></div><strong :style="{ color: dimension.color }">{{ dimension.average }}</strong></header>
            <div class="dimension-average-track"><span :style="{ width: `${dimension.average}%`, background: dimension.color }"/></div>
            <div class="distribution-row"><div v-for="bin in dimension.distribution" :key="bin.label"><b>{{ bin.count }}</b><small>{{ bin.label }}</small></div></div>
          </article>
        </div>
        <p v-if="!analytics.dimensions.length" class="analytics-empty">这份问卷暂未配置计分维度。</p>
      </section>

      <section v-else-if="activeSection === 'responses'" class="analytics-section">
        <div class="analytics-section-tools"><div><h2>单份答卷管理</h2><p>排除和恢复都会保留原始答案并写入审计记录。</p></div><button class="button button-ghost" :disabled="responseLoading" @click="loadResponses"><RefreshCw/>刷新</button></div>
        <div v-if="responseLoading" class="analytics-loading compact">正在读取答卷…</div>
        <div v-else-if="responses?.rows.length" class="response-table-wrap">
          <table class="response-table">
            <thead><tr><th>答卷</th><th>版本</th><th>完成时间</th><th>用时</th><th>来源</th><th>质量状态</th><th>标记原因</th><th>操作</th></tr></thead>
            <tbody>
              <tr v-for="row in responses.rows" :key="row.id">
                <td data-label="答卷"><code>{{ row.id.slice(0, 8) }}</code></td>
                <td data-label="版本">V{{ row.version }} · {{ row.variant }}</td>
                <td data-label="完成时间">{{ new Date(row.completedAt).toLocaleString('zh-CN') }}</td>
                <td data-label="用时">{{ row.durationSeconds }}s</td>
                <td data-label="来源">{{ row.metadata.collectionChannel === 'offline_package' ? '离线包' : row.metadata.resumedFromDraft ? '线上 · 恢复过' : '线上' }}</td>
                <td data-label="质量状态"><span :class="`quality-pill ${row.qualityStatus}`">{{ qualityLabels[row.qualityStatus] }}</span></td>
                <td data-label="标记原因"><span v-if="row.qualityFlags.length">{{ row.qualityFlags.map((flag) => flagLabels[flag.code] || flag.message).join('；') }}</span><span v-else>—</span></td>
                <td data-label="操作"><button v-if="row.qualityStatus !== 'excluded'" class="table-danger-button" @click="reviewQuality(row, 'excluded')"><Trash2/>排除</button><button v-else class="table-restore-button" @click="reviewQuality(row, 'normal')"><RotateCcw/>恢复</button></td>
              </tr>
            </tbody>
          </table>
          <div class="table-pagination"><button :disabled="responses.page <= 1" @click="changePage(responses.page - 1)"><ChevronLeft/>上一页</button><span>第 {{ responses.page }} / {{ responses.pages }} 页 · 共 {{ responses.total }} 份</span><button :disabled="responses.page >= responses.pages" @click="changePage(responses.page + 1)">下一页<ChevronRight/></button></div>
        </div>
        <p v-else class="analytics-empty">当前筛选范围内还没有答卷。</p>
      </section>

      <section v-else-if="activeSection === 'exports'" class="analytics-section">
        <div class="export-control-card">
          <div><h2>导出范围</h2><p>默认排除被创作者标记为“已排除”的答卷；原始答案始终保留在数据库中。</p></div>
          <label class="analytics-checkbox"><input v-model="includeExcluded" type="checkbox"/><span>导出时包含已排除答卷</span></label>
        </div>
        <div class="export-card-grid">
          <button @click="exportCsv"><Download/><strong>CSV 原始数据</strong><span>适合 Excel、SPSS 前处理和通用归档</span></button>
          <button @click="downloadJson(`/api/creator/surveys/${surveyId}/export.json`, `${survey?.title}-项目.json`)"><FileJson/><strong>JSON 项目与版本</strong><span>保留问卷结构、版本与审核信息</span></button>
          <button v-if="survey?.importFormat && !localMode" @click="downloadFile(`/api/creator/surveys/${surveyId}/export-source?includeExcluded=${includeExcluded}`, `${survey.title}-源格式答案.${survey.importFormat}`)"><FileOutput/><strong>按源格式导出</strong><span>依据导入映射恢复字段顺序与工作表</span></button>
          <button @click="exportDimensionChart"><BarChart3/><strong>维度图 PNG</strong><span>生成不含个人答案的汇总图片</span></button>
        </div>
      </section>

      <section v-else class="analytics-section" id="versions">
        <div class="analytics-section-tools"><div><h2>版本记录</h2><p>回滚只会把历史版本复制为新草稿，不覆盖线上版本和历史答卷。</p></div><History/></div>
        <div class="version-timeline">
          <article v-for="item in versions" :key="item.id">
            <span>V{{ item.version }}</span>
            <div><h3>{{ item.published_at ? '已发布版本' : '审核或草稿版本' }}</h3><p>{{ item.change_note || '未填写版本说明' }} · {{ new Date(item.created_at).toLocaleString('zh-CN') }}</p></div>
            <button class="button button-ghost" @click="rollback(item.version)"><RotateCcw/>恢复为草稿</button>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>
